import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode } from "react";
import { UsePaginationInstanceProps, UsePaginationState } from "react-table";

import { BorderlessButton } from "components/customInputs/buttons/Button";
import SelectInput from "components/customInputs/SelectInput";

interface AdminPaginationPluginProps<
  D extends Record<string, unknown> = Record<string, unknown>
> extends Omit<UsePaginationInstanceProps<D>, "page" | "pageOptions">,
    UsePaginationState<D> {
  // Take most of the same options generated by useTable hooks
  // We don't need "page" (an array of rows on the page)
  // ... nor pageOptions
}
export interface AdminPaginationProps extends AdminPaginationPluginProps {
  totalCount?: number;
  label?: string;
  pageSizes?: number[];
  toggleAllRowsExpanded: Function;
  setToLocalStorage?: Function;
  manualReviewCount?: number;
  idSuffix?: string;
  usePageSizeSelect?: boolean;
  children?: ReactNode;
}

const AdminPagination = ({
  nextPage,
  previousPage,
  gotoPage,
  canPreviousPage,
  canNextPage,
  totalCount,

  pageIndex,
  pageCount,
  pageSize,
  pageSizes = [10, 25],
  setPageSize,

  label = "cases",

  toggleAllRowsExpanded,
  setToLocalStorage,
  manualReviewCount,
  idSuffix = "lower",
  usePageSizeSelect = true,
  children,
}: AdminPaginationProps) => {
  const onClickNextPage = () => {
    toggleAllRowsExpanded(false);
    nextPage();
  };

  const onClickPreviousPage = () => {
    toggleAllRowsExpanded(false);
    previousPage();
  };

  const onChangePage = (page: number) => {
    toggleAllRowsExpanded(false);
    gotoPage(page);
  };

  return (
    <div className="AdminCaselist__pagination">
      <div>{children}</div>
      <div>
        {usePageSizeSelect && (
          <SelectInput
            id="pageSizeSelect"
            testId="pageSizeSelect"
            value={`${pageSize}`}
            onChange={(e: any) => {
              setPageSize(Number(e.target.value));
              setToLocalStorage && setToLocalStorage(e.target.value);
            }}
            options={pageSizes.map((pageSize) => ({
              value: `${pageSize}`,
              label: `Show ${pageSize} per page`,
            }))}
            disableDefault
          />
        )}

        <span>
          <strong>
            Page {pageIndex + 1 || 1} of {pageCount === -1 ? 1 : pageCount}
          </strong>{" "}
          <span>
            ({totalCount} {label})
            {manualReviewCount &&
              ` - ${manualReviewCount} slides in manual review`}
          </span>
        </span>
        <BorderlessButton
          onClick={() => onChangePage(0)}
          testId={`pagination-first-page-${idSuffix}`}
          title="First Page"
          disabled={!canPreviousPage}
        >
          <FontAwesomeIcon icon="angles-left" />
        </BorderlessButton>
        <BorderlessButton
          onClick={onClickPreviousPage}
          testId={`pagination-previous-page-${idSuffix}`}
          title="Previous"
          disabled={!canPreviousPage}
        >
          <FontAwesomeIcon icon="angle-left" />
        </BorderlessButton>
        <BorderlessButton
          onClick={onClickNextPage}
          testId={`pagination-next-page-${idSuffix}`}
          title="Next"
          disabled={!canNextPage}
        >
          <FontAwesomeIcon icon="angle-right" />
        </BorderlessButton>
        <BorderlessButton
          onClick={() => onChangePage(pageCount - 1)}
          testId={`pagination-last-page-${idSuffix}`}
          title="Last Page"
          disabled={!canNextPage}
        >
          <FontAwesomeIcon icon="angles-right" />
        </BorderlessButton>
      </div>
    </div>
  );
};

export default AdminPagination;
